/**
 * EventCheckInPage - Mobile-First Event Check-In
 * 
 * Enhanced event check-in flow with method selection,
 * location verification, and beautiful success/error states.
 * 
 * Reference: docs/design/FIGMA_IMPLEMENTATION_PLAN.md - Phase 7E
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useEvent } from '../hooks/useEvents';
import { useCheckIn } from '../hooks/useCheckins';
import { TopBar, Button, EmptyState, OroLoader } from '../components';
import { QrCode, MapPin, Key, CheckCircle, XCircle, Clock, Users, AlertTriangle, Calendar } from 'lucide-react';
import type { ApiError } from '../lib/api';
import { getCurrentPosition } from '../lib/geoUtils';
import { eventCanVerifyGps } from '../lib/checkinPermissions';

type CheckInMethod = {
  id: 'qr' | 'gps' | 'secret';
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  enabled: boolean;
  description?: string;
};

type CheckInState = 'selecting' | 'processing' | 'success' | 'error';

export default function EventCheckInPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [checkInState, setCheckInState] = useState<CheckInState>('selecting');
  const [selectedMethod, setSelectedMethod] = useState<'qr' | 'gps' | 'secret' | null>(null);
  const [secretWord, setSecretWord] = useState('');
  // GPS-specific UI state: 'Locating you…' phase + permission/timeout errors (#1810)
  const [isLocating, setIsLocating] = useState(false);
  const [geolocationError, setGeolocationError] = useState<string | null>(null);
  
  // Parse URL parameters
  const sourceParam = searchParams.get('source') || 'manual';
  const autoCheckIn = sourceParam === 'qr' || sourceParam === 'offline';
  
  // Fetch event data
  const { data: eventData, isLoading: eventLoading, error: eventError } = useEvent(id || '');
  const event = eventData;

  // Check-in mutation. The :id route param may be a short_code (e.g. QR/offline
  // entry), but POST /events/:id/checkins queries `WHERE id = ?` (UUID) — so
  // target the resolved event.id, matching EventDetailPage's sub-resource calls.
  const checkIn = useCheckIn(event?.id || '');

  // Auto check-in when coming from QR or offline
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    if (!id || !autoCheckIn) return;
    if (eventLoading || eventError) return;
    if (checkIn.isSuccess || checkIn.isPending || checkIn.isError) return;

    setCheckInState('processing');
    setSelectedMethod(sourceParam as 'qr');
    
    checkIn.mutate({
      source: sourceParam as 'qr' | 'offline',
    });
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [id, autoCheckIn, sourceParam, eventLoading, eventError, checkIn]);

  // Update state based on check-in status
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    if (checkIn.isSuccess) {
      setCheckInState('success');
    } else if (checkIn.isError) {
      setCheckInState('error');
    }
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [checkIn.isSuccess, checkIn.isError]);

  const checkInMethods: CheckInMethod[] = [
    {
      id: 'qr',
      title: 'QR Check-In',
      subtitle: 'Scan the event QR code',
      icon: <QrCode className="w-8 h-8 text-accent" />,
      enabled: true,
      description: 'Quick and easy - just point your camera at the event QR code'
    },
    {
      id: 'gps',
      title: 'GPS Check-In',
      subtitle: "Check in when you're at the location",
      icon: <MapPin className="w-8 h-8 text-accent" />,
      // #3017: same predicate as CheckinButton, the leader panels, native, and
      // the backend. This surface had two independent defects: it ignored
      // `checkin_radius_m` entirely (so an event with coordinates but no radius
      // offered GPS, requested OS location, and then hit a backend rejection),
      // and it tested TRUTHINESS, so a legitimate 0 coordinate — Null Island —
      // read as absent. Found by Frank on PR #3027.
      enabled: !!event && eventCanVerifyGps(event),
      description: 'Automatic check-in when you arrive at the event location'
    },
    {
      id: 'secret',
      title: 'Secret Word Check-In',
      subtitle: 'Enter the check-in code',
      icon: <Key className="w-8 h-8 text-accent" />,
      enabled: event?.checkin_method === 'secret_word', // #1117
      description: 'Get the secret word from event organizers'
    }
  ];

  const handleMethodSelect = async (method: 'qr' | 'gps' | 'secret') => {
    if (!id || !event) return;
    
    setSelectedMethod(method);
    setCheckInState('processing');

    try {
      if (method === 'qr') {
        // Navigate to QR scanner
        navigate('/scan-qr');
        return;
      }
      
      if (method === 'gps') {
        // GPS check-in requires real coordinates (#1810). Prompt the browser
        // for the user's position, then mutate. Permission-denied / timeout /
        // unavailable surface as a geolocation-specific error.
        setIsLocating(true);
        setGeolocationError(null);
        const result = await getCurrentPosition();
        setIsLocating(false);

        if (!result.success || !result.coordinates) {
          setGeolocationError(result.error?.message || 'Unable to determine your location');
          setCheckInState('error');
          return;
        }

        checkIn.mutate({
          source: 'gps',
          lat: result.coordinates.lat,
          lng: result.coordinates.lng,
        });
        return;
      }
      
      if (method === 'secret') {
        // Secret word check-in handled by input form below
        // setCheckInState remains 'processing' to show input form
        return;
      }
    } catch {
      setCheckInState('error');
    }
  };

  const handleSecretWordSubmit = async () => {
    if (!secretWord.trim()) {
      return;
    }

    try {
      checkIn.mutate({
        source: 'secret_word',
        secret_word: secretWord.trim(),
      });
    } catch {
      setCheckInState('error');
    }
  };

  const formatEventDateTime = (timestamp: number): string => {
    return new Date(timestamp * 1000).toLocaleString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short', // #1655 — match notification copy ("8:30 PM EDT")
    });
  };

  // Loading state
  if (eventLoading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Check In" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-12 text-center">
            <OroLoader className="mx-auto mb-4" decorative />
            <p className="text-text-muted">Loading event...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error loading event
  if (eventError || !event || !id) {
    const errorMessage = eventError 
      ? (eventError as unknown as ApiError).message || 'Failed to load event'
      : !event 
      ? 'Event not found'
      : 'Invalid event ID';
      
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Check In" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-12">
            <EmptyState
              icon={<Calendar className="w-12 h-12" />}
              title="Unable to check in"
              message={errorMessage}
              actionLabel='Go Back' onAction={() => navigate(-1)}
            />
          </div>
        </div>
      </div>
    );
  }

  // Check-in success state
  if (checkInState === 'success') {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Checked In!" />
        <div className="flex-1 overflow-y-auto pt-14 flex items-center justify-center px-4">
          <div className="max-w-sm w-full">
            {/* Success Animation */}
            <div className="text-center mb-6">
              <div className="w-24 h-24 bg-oro-success/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-oro-success" />
              </div>
              <h2 className="text-2xl font-bold text-oro-success mb-2">You're Checked In!</h2>
              <p className="text-text-muted">Welcome to the event</p>
            </div>

            {/* Event Info Card */}
            <div className="bg-oro-surface border border-oro-success/20 rounded-xl p-4 mb-6">
              <h3 className="font-semibold text-text-primary mb-2">{event.title}</h3>
              {event.description && (
                <p className="text-text-secondary text-sm mb-3 line-clamp-2">{event.description}</p>
              )}
              <div className="flex items-center gap-4 text-sm text-text-muted">
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{formatEventDateTime(event.starts_at)}</span>
                </div>
              </div>
              {event.location_name && (
                <div className="flex items-center gap-1 text-sm text-text-muted mt-2">
                  <MapPin className="w-4 h-4" />
                  <span>{event.location_name}</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <Button
                onClick={() => navigate(`/event/${id}`)}
                variant="primary"
                className="w-full"
              >
                View Event Details
              </Button>
              <Button
                onClick={() => navigate('/home')}
                variant="secondary"
                className="w-full"
              >
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check-in error state
  if (checkInState === 'error') {
    const errorMessage = geolocationError
      ? geolocationError
      : checkIn.error
      ? (checkIn.error as unknown as ApiError).message || 'Failed to check in'
      : 'Something went wrong during check-in';

    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Check In Failed" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14 flex items-center justify-center px-4">
          <div className="max-w-sm w-full">
            {/* Error State */}
            <div className="text-center mb-6">
              <div className="w-24 h-24 bg-oro-error/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-12 h-12 text-oro-error" />
              </div>
              <h2 className="text-2xl font-bold text-oro-error mb-2">Check-In Failed</h2>
              <p className="text-text-muted mb-4">We couldn't check you in to this event</p>

              {/* Error Details */}
              <div className="bg-oro-error/10 border border-oro-error/20 rounded-lg p-3 mb-6">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-oro-error shrink-0 mt-0.5" />
                  <p className="text-oro-error text-sm text-left">{errorMessage}</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              <Button
                onClick={() => {
                  setCheckInState('selecting');
                  setSelectedMethod(null);
                  setGeolocationError(null);
                }}
                variant="primary"
                className="w-full"
              >
                Try Different Method
              </Button>
              <Button
                onClick={() => navigate(`/event/${id}`)}
                variant="secondary"
                className="w-full"
              >
                View Event Details
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Processing state
  if (checkInState === 'processing') {
    const methodName = selectedMethod === 'qr' ? 'QR Code' : 
                      selectedMethod === 'gps' ? 'GPS Location' :
                      selectedMethod === 'secret' ? 'Secret Word' : 'Unknown';

    // Show secret word input form
    if (selectedMethod === 'secret') {
      return (
        <div className="flex flex-col bg-oro-bg">
          <TopBar title="Secret Word Check-In" onBack={() => {
            setCheckInState('selecting');
            setSelectedMethod(null);
            setSecretWord('');
          }} />
          <div className="flex-1 overflow-y-auto pt-14 px-4 py-6">
            {/* Event Info Card */}
            <div className="bg-oro-surface border border-oro-border rounded-xl p-4 mb-6">
              <div className="text-center">
                <h2 className="font-semibold text-text-primary mb-1">{event.title}</h2>
                <div className="flex items-center justify-center gap-1 text-text-muted text-sm">
                  <Clock className="w-4 h-4" />
                  <span>{formatEventDateTime(event.starts_at)}</span>
                </div>
              </div>
            </div>

            {/* Secret Word Input */}
            <div className="bg-oro-surface border border-oro-border rounded-xl p-6 mb-6">
              <div className="text-center mb-6">
                <Key className="w-12 h-12 text-accent mx-auto mb-3" />
                <h3 className="font-semibold text-text-primary mb-2">Enter Secret Word</h3>
                <p className="text-text-muted text-sm">
                  Enter the secret word provided by the event organizers
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <input
                    type="text"
                    value={secretWord}
                    onChange={(e) => setSecretWord(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSecretWordSubmit()}
                    placeholder="Secret word"
                    className="w-full px-4 py-3 bg-oro-bg border border-oro-border rounded-lg 
                             text-text-primary placeholder-text-muted
                             focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent
                             transition-colors"
                    autoFocus
                    disabled={checkIn.isPending}
                  />
                </div>

                <button
                  onClick={handleSecretWordSubmit}
                  disabled={!secretWord.trim() || checkIn.isPending}
                  className="w-full py-3 px-4 bg-accent hover:bg-accent-light
                           disabled:bg-oro-surface-alt disabled:text-text-muted
                           text-text-primary font-medium rounded-lg
                           transition-all duration-200
                           disabled:cursor-not-allowed"
                >
                  {checkIn.isPending ? (
                    <div className="flex items-center justify-center gap-2">
                      <OroLoader variant="inline" decorative />
                      <span>Checking In...</span>
                    </div>
                  ) : (
                    'Check In'
                  )}
                </button>
              </div>

              {checkIn.error && (
                <div className="mt-4 p-3 bg-oro-error/10 border border-oro-error/20 rounded-lg">
                  <p className="text-oro-error text-sm text-center">
                    {checkIn.error.message || 'Check-in failed. Please try again.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Default processing UI for other methods. GPS has a "Locating you…"
    // phase before the mutation fires (#1810).
    const isLocatingPhase = selectedMethod === 'gps' && isLocating;
    const topBarTitle = isLocatingPhase ? 'Locating You...' : 'Checking In...';
    const heading = isLocatingPhase ? 'Finding your location...' : 'Checking you in...';
    const subtext = isLocatingPhase
      ? 'Please allow location access when prompted'
      : `Using ${methodName}`;

    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title={topBarTitle} />
        <div className="flex-1 overflow-y-auto pt-14 flex items-center justify-center px-4">
          <div className="text-center">
            <OroLoader size={80} className="mx-auto mb-6" decorative />
            <h2 className="text-xl font-semibold text-text-primary mb-2">{heading}</h2>
            <p className="text-text-muted">{subtext}</p>
          </div>
        </div>
      </div>
    );
  }

  // Method selection state (default)
  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar title="Check In" onBack={() => navigate(-1)} />
      
      <div className="flex-1 overflow-y-auto pt-14 px-4 py-6">
        {/* Event Info Card */}
        <div className="bg-oro-surface border border-oro-border rounded-xl p-4 mb-6">
          <div className="text-center">
            <h2 className="font-semibold text-text-primary mb-1">{event.title}</h2>
            <div className="flex items-center justify-center gap-1 text-text-muted text-sm mb-2">
              <Clock className="w-4 h-4" />
              <span>{formatEventDateTime(event.starts_at)}</span>
            </div>
            {event.location_name && (
              <div className="flex items-center justify-center gap-1 text-text-secondary text-sm">
                <MapPin className="w-4 h-4" />
                <span>{event.location_name}</span>
              </div>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="mb-6">
          <h3 className="font-semibold text-text-primary mb-2">Choose Check-In Method</h3>
          <p className="text-text-muted text-sm leading-relaxed">
            Select how you'd like to check in to this event. Event organizers will specify which method to use.
          </p>
        </div>

        {/* Check-In Method Cards */}
        <div className="space-y-3 mb-6">
          {checkInMethods.map((method) => (
            <button
              key={method.id}
              onClick={() => method.enabled ? handleMethodSelect(method.id) : null}
              disabled={!method.enabled || checkIn.isPending}
              className={`w-full rounded-xl p-4 text-left transition-all duration-200 ${
                method.enabled 
                  ? 'bg-oro-surface border border-oro-border hover:border-accent/50 hover:shadow-md group' 
                  : 'bg-oro-surface/50 border border-oro-border/50 opacity-50 cursor-not-allowed'
              }`}
            >
              <div className="flex items-center gap-4">
                {/* Icon */}
                <div className={`shrink-0 w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                  method.enabled 
                    ? 'bg-oro-surface-alt border border-oro-border group-hover:border-accent/30' 
                    : 'bg-oro-surface border border-oro-border/50'
                }`}>
                  {method.icon}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-text-primary mb-1">{method.title}</h3>
                  <p className="text-text-muted text-sm line-clamp-1">{method.subtitle}</p>
                  {method.description && (
                    <p className="text-text-muted/80 text-xs mt-1 line-clamp-1">{method.description}</p>
                  )}
                </div>

                {/* Arrow */}
                {method.enabled && (
                  <div className="shrink-0 text-text-muted group-hover:text-accent transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Helper Text */}
        <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center shrink-0">
              <Users className="w-4 h-4 text-accent" />
            </div>
            <div>
              <p className="text-text-secondary text-sm leading-relaxed">
                <strong>Need help?</strong> Ask an event organizer or volunteer which check-in method to use. 
                They'll help you get checked in quickly.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
